import React, { memo, useMemo, useRef, useCallback } from 'react'
import FlashIcon from '../../assets/icons/Flash'
import { useDispatch, useSelector } from 'react-redux'
import { Box, MenuItem, Tooltip, Typography } from '@mui/material'
import { AppMenu } from '../common/AppMenu'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import QuickWidgetMenuItem from './quickWidgetMenuItem';
import { selectQuickWidgets } from '../../selectors/quickWidgets';
import { QuickWidgetConfig, QuickWidgetTypes, updateUserPreferencesInState } from '../../reducer/user.reducer'
import { selectUserPreferences } from '../../selectors/user'
import { ExtraSmallButton, NonInteractiveTooltip } from '../custom'
import { mergeSideBarBottomButtonSx } from '../sideBarRight/sideBarBottomIconButtonSx'
import { getQuickWidgetConfigs } from '../../util/quickWidgets'
import ToggleSwitch from '../common/toggleSwitch/toggleSwitch'
import useShortcuts from '../../hooks/useShortcuts'
import { ShortcutsCategories, ShortcutType } from '../KeyboardShortcuts/Shortcuts.types'
import { QuickWidgetShortcutsKeyName } from '../KeyboardShortcuts/ShortcutsKeymap'

const QuickWidgetsMenu: React.FC = () => {
    const dispatch = useDispatch()
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const container = useSelector(selectMultiPaneFullScreen)
    const open = Boolean(anchorEl)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const widgets = useSelector(selectQuickWidgets)
    const userPreferences = useSelector(selectUserPreferences)

    const widgetsVisible = useMemo(() => (
        Object.values(widgets || {}).every(w => w?.visible !== false)
    ), [widgets])

    const widgetActive = useMemo(() => (
        !!Object.values(widgets || {}).find((w) => (w as QuickWidgetConfig)?.enabled)
    ), [widgets])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const handleButtonClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (open) {
            handleClose()
        } else {
            setAnchorEl(event.currentTarget)
        }
    }, [open, handleClose])

    const handleToggle = useCallback(() => {
        const newValue = !widgetsVisible;
        const updatedWidgets = { ...userPreferences.quickWidgets };

        type WidgetKey = keyof typeof updatedWidgets;
        (Object.keys(updatedWidgets) as WidgetKey[]).forEach((key) => {
            const widget = updatedWidgets[key];
            if (widget) {
                updatedWidgets[key] = {
                    ...widget,
                    visible: newValue
                };
            }
        });

        dispatch(updateUserPreferencesInState({
            ...userPreferences,
            quickWidgets: updatedWidgets
        }));
    }, [dispatch, userPreferences, widgetsVisible])

    const toggleWidget = useCallback((type: QuickWidgetTypes) => {
        if (!widgetsVisible) return;
        const updatedWidgets = { ...(userPreferences.quickWidgets || {}) };
        const existing = widgets?.[type] as QuickWidgetConfig;
        const isCurrentlyEnabled = !!existing?.enabled;
        const newConfig = getQuickWidgetConfigs(type, widgets || {}, true)[type];
        const currentMaxOrder = Math.max(
            0,
            ...Object.values(updatedWidgets)
                .filter((w) => w?.enabled)
                .map((w) => w?.order || 0)
        );
        const updatedWidget: QuickWidgetConfig = {
            ...existing,
            enabled: !isCurrentlyEnabled,
            ...newConfig,
            ...(isCurrentlyEnabled ? {} : { order: currentMaxOrder + 1 })
        };

        dispatch(updateUserPreferencesInState({
            ...userPreferences,
            quickWidgets: {
                ...(userPreferences.quickWidgets || {}),
                [type]: updatedWidget
            }
        }))
    }, [widgetsVisible, userPreferences, widgets, dispatch])


    const shortcuts: ShortcutType = useMemo(() => ({
        [QuickWidgetShortcutsKeyName.showHideQuickWidgets]: handleToggle,
        [QuickWidgetShortcutsKeyName.openQuickWidgetMarketDepth]: () => toggleWidget(QuickWidgetTypes.MARKET_DEPTH),
        [QuickWidgetShortcutsKeyName.openQuickWidgetOrders]: () => toggleWidget(QuickWidgetTypes.ORDER),
        [QuickWidgetShortcutsKeyName.openQuickWidgetPositions]: () => toggleWidget(QuickWidgetTypes.POSITION),
        [QuickWidgetShortcutsKeyName.openQuickTradeWidget]: () => toggleWidget(QuickWidgetTypes.QUICK_TRADE)
    }), [handleToggle, toggleWidget])

    useShortcuts(ShortcutsCategories.QUICK_WIDGETS, shortcuts)

    return (
        <div>
            <Tooltip title={<span style={{ fontSize: '12px' }}>Quick Widgets</span>} arrow placement="left">
                <span>
                    <ExtraSmallButton
                        ref={buttonRef}
                        size="small"
                        color="inherit"
                        onClick={handleButtonClick}
                        data-event-source="right-sidebar"
                        data-event={'Quick Widgets Menu Opened'}
                        sx={mergeSideBarBottomButtonSx(theme => ({
                            backgroundColor: open ? theme.palette.action.selected : 'transparent'
                        }))}
                    >
                        <FlashIcon />
                    </ExtraSmallButton>
                </span>
            </Tooltip>

            <AppMenu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                clickAwayIgnoreRefs={[buttonRef]}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                transitionDuration={0}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <MenuItem>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography component="span" sx={{ lineHeight: '24px', backgroundColor: 'unset', fontSize: 12, opacity: 0.5, paddingLeft: 0 }}>
                            QUICK WIDGETS
                        </Typography>
                        <NonInteractiveTooltip title="⇧ + Q" arrow placement="top">
                            <ToggleSwitch
                                size="small"
                                checked={widgetsVisible}
                                disableRipple
                                onChange={handleToggle}
                            />
                        </NonInteractiveTooltip>
                    </Box>
                </MenuItem>

                <QuickWidgetMenuItem widgetType={QuickWidgetTypes.MARKET_DEPTH} leftPadding={widgetActive} text="Market Depth" closeMenu={handleClose} disabled={!widgetsVisible} shortcutHint="D" />
                <QuickWidgetMenuItem widgetType={QuickWidgetTypes.ORDER} leftPadding={widgetActive} text="Orders" closeMenu={handleClose} disabled={!widgetsVisible} shortcutHint="O" />
                <QuickWidgetMenuItem widgetType={QuickWidgetTypes.POSITION} leftPadding={widgetActive} text="Positions" closeMenu={handleClose} disabled={!widgetsVisible} shortcutHint="P" />
                <QuickWidgetMenuItem widgetType={QuickWidgetTypes.QUICK_TRADE} leftPadding={widgetActive} text="Quick Trade" closeMenu={handleClose} disabled={!widgetsVisible} shortcutHint="T" />
            </AppMenu>
        </div>
    )
}

export default memo(QuickWidgetsMenu)
